import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { LoaderService, Tab } from '@shared';
import { ChangeDetectorRef } from '@angular/core';
import { GlobalEventsService } from '@shared-data';
import { TokenService } from '@auth';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  tabs: Tab[] = [];
  selectedTabIndex?: number = undefined;

  isLoading: boolean = false;

  tierSelectorVisible: number = 0;

  isAdminUser: boolean | null = null;

  constructor(
    private globalEventsService: GlobalEventsService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loaderService: LoaderService,
    private tokenService: TokenService,
  ) {}

  async ngOnInit() {
    this.loaderService.show();
    this.isAdminUser = await this.tokenService.isAdmin();
    const isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();

    this.tabs = [
      ...(this.isAdminUser
        ? [
            new Tab('Admin', 'admin/category'),
            new Tab('Reports', 'reports/failures'),
          ]
        : [
            // Hide Price Tiers and Product Pricing for AvailabilityOnly users
            ...(isAvailabilityOnly
              ? [
                  new Tab(
                    'Product Availability',
                    'restaurantitem/availability',
                  ),
                  new Tab('Reports', 'reports/sync-exceptions'),
                ]
              : [
                  new Tab('Price Tiers', 'tier'),
                  new Tab('Product Pricing', 'restaurantitem/pricing'),
                  new Tab(
                    'Product Availability',
                    'restaurantitem/availability',
                  ),
                  new Tab('Reports', 'reports/exceptions/delivery/negative'),
                ]),
          ]),
    ];
    this.subscribeToRouteChange();

    this.subscribeToLoader();

    this.subscribeToTierSelectorVisible();

    this.loaderService.hide();
  }

  subscribeToRouteChange() {
    const currentUrl = this.router.url;
    const isRoot = currentUrl === '/' || currentUrl.startsWith('/?');
    const savedRoute = sessionStorage.getItem('route');
    const canRestore =
      !!savedRoute &&
      savedRoute !== '/' &&
      !savedRoute.startsWith('/error') &&
      !savedRoute.startsWith('/?');

    if (isRoot && canRestore) {
      this.syncSelectedTab(savedRoute!);
      this.router.navigateByUrl(savedRoute!);
    } else if (isRoot) {
      this.onTabSelect(this.tabs[0]);
    } else {
      this.syncSelectedTab(currentUrl);
    }

    this.router.events.subscribe((routeEvent) => {
      if (routeEvent instanceof NavigationEnd) {
        const currentRoutePrefix =
          this.router.routerState.snapshot.url.slice(1);
        this.selectedTabIndex = this.tabs.findIndex((tab) => {
          return currentRoutePrefix.startsWith(tab.contentUrl);
        });
      }
    });
  }

  private syncSelectedTab(url: string) {
    const prefix = url.slice(1);
    const index = this.tabs.findIndex((tab) => prefix.startsWith(tab.contentUrl));
    this.selectedTabIndex = index;
    if (index >= 0) {
      this.tabs[index].selected = true;
    }
  }

  subscribeToLoader() {
    this.loaderService.isLoading$.subscribe((loading: number) => {
      this.isLoading = loading > 0;
      this.cdr.detectChanges();
    });
  }

  subscribeToTierSelectorVisible() {
    this.globalEventsService.tierSelectorVisible$.subscribe((visible) => {
      this.tierSelectorVisible = visible;
      this.cdr.detectChanges();
    });
  }

  onTabSelect(selectedTab: Tab) {
    const newIndex = this.tabs.indexOf(selectedTab);

    if (this.selectedTabIndex != newIndex) {
      this.selectedTabIndex = newIndex;
      selectedTab.selected = true;
      this.router.navigate([selectedTab?.contentUrl], {
        relativeTo: this.activatedRoute,
      });
    }
  }
}
